import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "dog-trainer-web-design-colorado",
  metaTitle: "Dog Trainer Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom dog trainer web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Dog trainer marketing", to: "/dog-trainer-marketing-colorado" },
  ],
  currentLabel: "Dog Trainer Web Design Colorado",
  eyebrow: "Dog Trainer Web Design — Colorado",
  h1: (
    <>
      Dog Trainer <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded dog trainer websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Dog Trainer websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first dog trainer sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Dog Trainer web design in Colorado",
    body: "Dog Trainer web design is the practice of building conversion-focused, mobile-first websites for dog trainer companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with video-ready hero sections." },
    { title: "Service pages per program", body: "Puppy, basic obedience, board-and-train, reactive dog, service dog, agility — each ranks independently." },
    { title: "Video testimonials", body: "Lazy-loaded client video testimonials with proper schema." },
    { title: "Booking + intake forms", body: "Behavior assessment intake forms with qualifying questions to filter for fit." },
    { title: "Trainer bio + credentials", body: "CPDT, KPA, IAABC credentials and methodology surfaced above the fold." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Methodology matters to the buyer", body: "Force-free, balanced, or e-collar trainers attract different clients. We surface methodology above the fold." },
    { heading: "Board-and-train is a 5-figure sale", body: "B&T programs deserve their own ranking page with proper photo/video proof, not a buried subpage." },
    { heading: "Reactive dog and service dog are niche gold", body: "These specialty searches have low competition and high conversion — most trainers miss them entirely." },
  ],
  relatedLinks: [
    { to: "/dog-trainer-marketing-colorado", label: "Dog trainer marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado dog trainer case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a dog trainer website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a dog trainer website?", a: "Typical timeline is 4–6 weeks for a 12–20 page dog trainer site with full service and service-area pages. Larger multi-location dog trainer companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs dog trainer companies?", a: "No — we build dog trainer sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Dog Trainer Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for dog trainer companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/dog-trainer-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
